import {
  Box,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';
import { DataGridPro, GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { BancoResponse, InvestidorVendaResponse, VendaLoteItemRequest } from 'common/genericTypes';
import { CpfCnpjMask } from 'components/masks';
import BancoAutoComplete from 'components/operacoes/bancoAutoComplete';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { AutoComplete, Button, Dropdown, MdSpace, NumberField, Section, SmSpace, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'store';
import VendaFeedbackModal, {
  VendaLoteResponse,
  VendaFeedbackData,
  ComprovanteUploadResponse,
} from './vendaFeedbackModal';
import {
  GETInvestidoresVenda,
  GETInstrumentosInvestidorVenda,
  GETCustodianteVenda,
  GETInvestidorDestinoVenda,
  POSTVendaLote,
  POSTVendaLoteComprovantes,
} from 'requests/operacao';
import { localizedTextsMap } from 'utils/dataGrid/dataGridUtils';
import { addCpfCnpjMask } from 'utils/mask';
import { formatIntNumber, formatNumber } from 'utils/number';
import { validaCpfCnpj, stripCpfCnpjFormatting } from 'utils/validation';
import { addAlert } from 'store/modules/snackBar';
import { v4 as uuid } from 'uuid';

interface InstrumentoDisponivel {
  id: string;
  codigo: string;
  serie: string;
  vencimento: string;
  saldoDisponivel: number;
}

interface DestinoCarrinho {
  id: string;
  custodianteCnpj: string;
  custodianteLabel: string;
  custodianteErro: string;
  investidorCpfCnpj: string;
  investidorLabel: string;
  investidorErro: string;
  banco: BancoResponse | null;
  modalidadeLiquidacao: '' | 'BRUTA' | 'DIRETA';
  quantidade: number;
  pu: number;
  comprovantes: File[];
}

interface IFCarrinho extends InstrumentoDisponivel {
  destinos: DestinoCarrinho[];
}

const CARRINHO_STORAGE_KEY = 'venda-cadastro-carrinho';

const modalidadesLiquidacaoOptions = [
  { value: 'BRUTA', description: 'Bruta' },
  { value: 'DIRETA', description: 'Direta' },
];

const useStyles = makeStyles((theme) => ({
  wideForm: {
    '& .MuiGrid-spacing-xs-4 > .MuiGrid-grid-lg-6': {
      flexBasis: '70%',
      maxWidth: '70%',
    },
    '& .MuiGrid-spacing-xs-4 > .MuiGrid-grid-lg-3': {
      flexBasis: '20%',
      maxWidth: '20%',
    },
    [theme.breakpoints.down('md')]: {
      '& .MuiGrid-spacing-xs-4 > .MuiGrid-grid-lg-6': {
        flexBasis: '100%',
        maxWidth: '100%',
      },
      '& .MuiGrid-spacing-xs-4 > .MuiGrid-grid-lg-3': {
        flexBasis: '100%',
        maxWidth: '100%',
      },
    },
  },
}));

const CadastrarOperacaoVenda: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const usuarioLogado = useSelector((state: RootState) => state.auth.usuario);
  const getBancosApi = useSelector((state: RootState) => state.catalogoApi.getBancos);

  const [investidores, setInvestidores] = useState<InvestidorVendaResponse[]>([]);
  const [investidorSelecionado, setInvestidorSelecionado] = useState<InvestidorVendaResponse | null>(null);
  const [bancos, setBancos] = useState<BancoResponse[]>([]);
  const [banco, setBanco] = useState<BancoResponse | null>(null);
  const isInitialMount = useRef(true);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [instrumentosDisponiveis, setInstrumentosDisponiveis] = useState<InstrumentoDisponivel[]>([]);
  const [pageSize, setPageSize] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState<VendaFeedbackData | null>(null);
  const [ifsCarrinho, setIfsCarrinho] = useState<IFCarrinho[]>(() => {
    try {
      const saved = localStorage.getItem(CARRINHO_STORAGE_KEY);
      return saved ? (JSON.parse(saved) as IFCarrinho[]) : [];
    } catch {
      return [];
    }
  });

  const ifsColumns: GridColDef[] = [
    { field: 'codigo', headerName: 'Código', flex: 1 },
    {
      field: 'saldoDisponivel',
      headerName: 'Quantidade Disponível',
      flex: 1,
      valueFormatter: ({ value }) => formatIntNumber(value as number),
    },
  ];

  const custodianteLabel = usuarioLogado?.participante
    ? `${usuarioLogado.participante.razaoSocial} - ${addCpfCnpjMask(usuarioLogado.participante.cnpj)}`
    : '';

  useEffect(() => {
    localStorage.setItem(CARRINHO_STORAGE_KEY, JSON.stringify(ifsCarrinho));
  }, [ifsCarrinho]);

  const fetchInvestidores = useCallback(async () => {
    try {
      const result = await GETInvestidoresVenda(dispatch);
      setInvestidores(result);
    } catch (error) {}
  }, [dispatch]);

  const fetchBancos = useCallback(async () => {
    try {
      const result = await getBancosApi(dispatch, false);
      setBancos(typeof result === 'string' ? [] : result.banks);
    } catch (error) {}
  }, [dispatch, getBancosApi]);

  useEffect(() => {
    fetchInvestidores();
    fetchBancos();
  }, [fetchInvestidores, fetchBancos]);

  const clearCarrinho = () => {
    setIfsCarrinho([]);
    localStorage.removeItem(CARRINHO_STORAGE_KEY);
    // SR-890: limpa também a chave legada do emissor que pode existir em sessões anteriores
    localStorage.removeItem('venda-cadastro-emissor');
  };

  const updateInvestidorSelecionado = (value: InvestidorVendaResponse | null) => {
    setInvestidorSelecionado(value);
    setInstrumentosDisponiveis([]);
    setSelectionModel([]);
    if (isInitialMount.current) return;
    if (ifsCarrinho.length > 0) {
      clearCarrinho();
    }
  };

  // SR-890: carrega IFs com base no investidor selecionado (antes era pelo emissor)
  useEffect(() => {
    if (!investidorSelecionado) {
      setInstrumentosDisponiveis([]);
      return;
    }

    const cpfCnpjLimpo = stripCpfCnpjFormatting(investidorSelecionado.cpfCnpj);
    let cancelled = false;

    const fetchIFsPorInvestidor = async () => {
      try {
        const result = await GETInstrumentosInvestidorVenda(dispatch, cpfCnpjLimpo, true);
        if (cancelled) return;

        // normaliza: o endpoint pode retornar array direto ou objeto wrapper { instrumentos: [...] }
        const lista = Array.isArray(result)
          ? result
          : (result as any)?.instrumentos ?? [];

        setInstrumentosDisponiveis(
          lista.map((inst: any, index: number) => ({
            id: inst.codigo ?? `inst-${index}`,
            codigo: inst.codigo ?? '',
            serie: inst.serie,
            vencimento: new Date(inst.dataVencimento).toLocaleDateString('pt-BR'),
            saldoDisponivel: inst.quantidade,
          }))
        );
      } catch (error: any) {
        if (cancelled) return;
        const msg =
          error?.response?.data?.message ??
          error?.response?.data?.errors?.join(', ') ??
          'Erro ao buscar instrumentos financeiros do investidor.';
        dispatch(addAlert({ severity: 'error', message: msg }));
        setInstrumentosDisponiveis([]);
      }
    };

    fetchIFsPorInvestidor();

    return () => {
      cancelled = true;
    };
  }, [investidorSelecionado, dispatch]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, []);

  const handleAdicionarSelecionados = () => {
    const novosIFs: IFCarrinho[] = instrumentosDisponiveis
      .filter((item) => selectionModel.includes(item.id))
      .filter((item) => !ifsCarrinho.some((c) => c.id === item.id))
      .map((item) => ({ ...item, destinos: [] }));
    if (novosIFs.length > 0) {
      setIfsCarrinho((prev) => [...prev, ...novosIFs]);
    }
  };

  const handleRemoverIF = (ifId: string) => {
    setIfsCarrinho((prev) => prev.filter((item) => item.id !== ifId));
  };

  const handleAdicionarDestino = (ifId: string) => {
    const novoDestino: DestinoCarrinho = {
      id: uuid(),
      custodianteCnpj: '',
      custodianteLabel: '',
      custodianteErro: '',
      investidorCpfCnpj: '',
      investidorLabel: '',
      investidorErro: '',
      banco: null,
      modalidadeLiquidacao: '',
      quantidade: 0,
      pu: 0,
      comprovantes: [],
    };
    setIfsCarrinho((prev) =>
      prev.map((item) => (item.id === ifId ? { ...item, destinos: [...item.destinos, novoDestino] } : item))
    );
  };

  const handleRemoverDestino = (ifId: string, destinoId: string) => {
    setIfsCarrinho((prev) =>
      prev.map((item) =>
        item.id === ifId ? { ...item, destinos: item.destinos.filter((d) => d.id !== destinoId) } : item
      )
    );
  };

  const handleUpdateDestino = (ifId: string, destinoId: string, updates: Partial<DestinoCarrinho>) => {
    setIfsCarrinho((prev) =>
      prev.map((item) =>
        item.id === ifId
          ? { ...item, destinos: item.destinos.map((d) => (d.id === destinoId ? { ...d, ...updates } : d)) }
          : item
      )
    );
  };

  const handleCustodianteCnpjChange = (ifId: string, destinoId: string, value: string) => {
    const masked = CpfCnpjMask(value ?? '');
    handleUpdateDestino(ifId, destinoId, { custodianteCnpj: masked, custodianteLabel: '', custodianteErro: '' });

    const cnpjLimpo = stripCpfCnpjFormatting(masked);
    if (cnpjLimpo.length === 14 && validaCpfCnpj(masked)) {
      GETCustodianteVenda(dispatch, cnpjLimpo)
        .then((result) => {
          handleUpdateDestino(ifId, destinoId, {
            custodianteLabel: `${result.razaoSocial} - ${addCpfCnpjMask(cnpjLimpo)}`,
            custodianteErro: '',
          });
        })
        .catch((error: any) => {
          const msg =
            error?.response?.data?.message ??
            error?.response?.data?.errors?.join(', ') ??
            'Erro ao buscar custodiante.';
          handleUpdateDestino(ifId, destinoId, { custodianteLabel: '', custodianteErro: msg });
        });
    } else if (cnpjLimpo.length >= 14) {
      handleUpdateDestino(ifId, destinoId, { custodianteErro: 'CNPJ inválido' });
    }
  };

  const handleInvestidorCpfCnpjChange = (ifId: string, destinoId: string, value: string) => {
    const masked = CpfCnpjMask(value ?? '');
    handleUpdateDestino(ifId, destinoId, { investidorCpfCnpj: masked, investidorLabel: '', investidorErro: '' });

    const limpo = stripCpfCnpjFormatting(masked);
    const completo = limpo.length === 11 || limpo.length === 14;
    if (completo && validaCpfCnpj(masked)) {
      GETInvestidorDestinoVenda(dispatch, limpo)
        .then((result) => {
          handleUpdateDestino(ifId, destinoId, {
            investidorLabel: `${result.razaoSocial} - ${addCpfCnpjMask(limpo)}`,
            investidorErro: '',
          });
        })
        .catch((error: any) => {
          const msg =
            error?.response?.data?.message ?? error?.response?.data?.errors?.join(', ') ?? 'Erro ao buscar investidor.';
          handleUpdateDestino(ifId, destinoId, { investidorLabel: '', investidorErro: msg });
        });
    } else if (limpo.length >= 11) {
      handleUpdateDestino(ifId, destinoId, { investidorErro: 'CPF/CNPJ inválido' });
    }
  };

  const calcAlocado = (destinos: DestinoCarrinho[]) => destinos.reduce((sum, d) => sum + d.quantidade, 0);

  const calcRestante = (item: IFCarrinho) => item.saldoDisponivel - calcAlocado(item.destinos);

  const calcTotalIF = (item: IFCarrinho) => item.destinos.reduce((sum, d) => sum + d.quantidade * d.pu, 0);

  const calcTotalDestino = (destino: DestinoCarrinho) => destino.quantidade * destino.pu;

  const isFormValid = () => {
    if (!investidorSelecionado) return false;
    if (!banco) return false;
    if (!usuarioLogado?.participante?.cnpj) return false;
    if (ifsCarrinho.length === 0) return false;
    return ifsCarrinho.every(
      (item) =>
        item.codigo &&
        calcRestante(item) >= 0 &&
        item.destinos.length > 0 &&
        item.destinos.every(
          (d) =>
            stripCpfCnpjFormatting(d.custodianteCnpj).length >= 11 &&
            stripCpfCnpjFormatting(d.investidorCpfCnpj).length >= 11 &&
            d.banco &&
            d.modalidadeLiquidacao &&
            d.quantidade > 0 &&
            d.pu > 0
        )
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid() || submitting) return;

    setSubmitting(true);

    const payload: VendaLoteItemRequest[] = ifsCarrinho.map((item) => ({
      operacaoCodigo: item.codigo,
      origem: {
        custodianteCnpj: stripCpfCnpjFormatting(usuarioLogado!.participante!.cnpj),
        cpfCnpjInvestidor: stripCpfCnpjFormatting(investidorSelecionado!.cpfCnpj),
        codigoBanco: banco!.code,
      },
      destinos: item.destinos.map((d) => ({
        custodianteCnpj: stripCpfCnpjFormatting(d.custodianteCnpj),
        cpfCnpjInvestidor: stripCpfCnpjFormatting(d.investidorCpfCnpj),
        contaDepositoDestino: '',
        codigoBanco: d.banco!.code,
        quantidade: d.quantidade,
        precoUnitario: d.pu,
        liquidacaoDireta: d.modalidadeLiquidacao === 'DIRETA',
      })),
    }));

    const comprovantesMap = new Map<string, File[]>();
    ifsCarrinho.forEach((item) => {
      item.destinos.forEach((d) => {
        if (d.comprovantes.length > 0) {
          const chave = `${item.codigo}|${stripCpfCnpjFormatting(d.custodianteCnpj)}|${stripCpfCnpjFormatting(d.investidorCpfCnpj)}`;
          comprovantesMap.set(chave, d.comprovantes);
        }
      });
    });

    try {
      const vendaResult = (await POSTVendaLote(dispatch, payload, true)) as VendaLoteResponse;

      let comprovanteResult: ComprovanteUploadResponse | undefined;

      const itensComComprovanteParaEnviar = vendaResult.itens
        .filter((item) => item.sucesso && item.operacaoId)
        .flatMap((item) => {
          const chave = `${item.operacaoCodigo}|${item.custodianteDestinoCnpj}|${item.investidorDestinoCpfCnpj}`;
          const arquivos = comprovantesMap.get(chave);
          if (!arquivos) return [];
          return arquivos.map((arquivo) => ({ operacaoId: item.operacaoId!, arquivo }));
        });

      if (itensComComprovanteParaEnviar.length > 0) {
        try {
          comprovanteResult = (await POSTVendaLoteComprovantes(
            dispatch,
            itensComComprovanteParaEnviar,
            true
          )) as ComprovanteUploadResponse;
        } catch (error: any) {
          const responseData = error?.response?.data;
          if (responseData?.status && responseData?.resumo && responseData?.itens) {
            comprovanteResult = responseData as ComprovanteUploadResponse;
          } else {
          comprovanteResult = {
            status: 'FALHA',
            mensagem: 'Erro ao enviar comprovantes',
            resumo: {
              totalProcessados: itensComComprovanteParaEnviar.length,
              totalSucesso: 0,
              totalFalha: itensComComprovanteParaEnviar.length,
            },
            itens: itensComComprovanteParaEnviar.map((i) => ({
              operacaoId: i.operacaoId,
              sucesso: false,
              comprovante: null,
              erro: { httpStatus: 500, mensagem: 'Erro de comunicação ao enviar comprovante' },
            })),
          };
          }
        }
      }

      setFeedbackData({ venda: vendaResult, comprovantes: comprovanteResult });
      setFeedbackOpen(true);
      clearCarrinho();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.errors?.join(', ') ??
        'Erro ao enviar a venda em lote.';
      dispatch(addAlert({ severity: 'error', message: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} className={classes.wideForm}>
      <Typography variant="h1">{'Venda'}</Typography>

      <Section title="Origem" isFulfilled={() => true} defaultGrid={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField label="Custodiante" value={custodianteLabel} disabled />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutoComplete
              label="Investidor"
              options={investidores}
              getOptionLabel={(option: InvestidorVendaResponse | null) => {
                if (!option) return '';
                return `${option.razaoSocial} - ${addCpfCnpjMask(option.cpfCnpj)}`;
              }}
              value={investidorSelecionado}
              onChange={(value: InvestidorVendaResponse | null) => updateInvestidorSelecionado(value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <BancoAutoComplete bancos={bancos} banco={banco} label="Banco" onChange={(value) => setBanco(value)} />
          </Grid>
        </Grid>
      </Section>

      <Section title="IFs Disponíveis" isFulfilled={() => true} defaultGrid={false}>
        <DataGridPro
          autoHeight
          localeText={localizedTextsMap}
          rows={instrumentosDisponiveis}
          columns={ifsColumns}
          checkboxSelection
          disableSelectionOnClick
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20]}
          pagination
          selectionModel={selectionModel}
          onSelectionModelChange={(newModel) => setSelectionModel(newModel)}
        />
        <SmSpace />
        <Grid container justify="flex-end">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={selectionModel.length === 0}
              onClick={handleAdicionarSelecionados}
            >
              {'Adicionar selecionados'}
            </Button>
          </Grid>
        </Grid>
        <MdSpace />
      </Section>

      <Section title="Carrinho de IFs" isFulfilled={() => true} defaultGrid={false}>
        {ifsCarrinho.length === 0 ? (
          <Box py={3} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              {'Nenhum IF adicionado ao carrinho.'}
            </Typography>
          </Box>
        ) : (
          ifsCarrinho.map((item) => (
            <Paper key={item.id} elevation={2} style={{ padding: '16px', marginBottom: '16px' }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box display="flex" style={{ gap: '32px' }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {'Código'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{item.codigo}</strong>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {'Série'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{item.serie}</strong>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {'Vencimento'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{item.vencimento}</strong>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {'Saldo'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{formatIntNumber(item.saldoDisponivel)}</strong>
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: '24px' }}>
                  <Box textAlign="right">
                    <Typography variant="caption" color="textSecondary">
                      {'Alocado'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{formatIntNumber(calcAlocado(item.destinos))}</strong>
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="textSecondary">
                      {'Restante'}
                    </Typography>
                    <Typography variant="body1" color={calcRestante(item) < 0 ? 'error' : 'textPrimary'}>
                      <strong>{formatIntNumber(calcRestante(item))}</strong>
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="textSecondary">
                      {'Total'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>{`R$ ${formatNumber(calcTotalIF(item))}`}</strong>
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoverIF(item.id)}>
                    <DeleteOutline />
                  </IconButton>
                </Box>
              </Box>

              <Box mt={2} mb={2}>
                <Divider />
              </Box>

              {calcRestante(item) < 0 && (
                <Box
                  mb={2}
                  p={1}
                  style={{ backgroundColor: '#fdecea', border: '1px solid #f44336', borderRadius: '4px' }}
                >
                  <Typography variant="body2" color="error">
                    {`Quantidade alocada (${formatIntNumber(
                      calcAlocado(item.destinos)
                    )}) ultrapassa o saldo disponível (${formatIntNumber(item.saldoDisponivel)}). Revise os destinos.`}
                  </Typography>
                </Box>
              )}

              {item.destinos.length === 0 ? (
                <Box py={2} textAlign="center">
                  <Typography variant="body2" color="textSecondary">
                    {'Adicione pelo menos um destino para este IF.'}
                  </Typography>
                </Box>
              ) : (
                item.destinos.map((destino, destinoIndex) => (
                  <Box key={destino.id} mb={2} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                    <Grid container alignItems="center" style={{ marginBottom: '8px' }}>
                      <Grid item xs>
                        <Typography variant="subtitle2">
                          <strong>{`Destino ${destinoIndex + 1}`}</strong>
                        </Typography>
                      </Grid>
                      <Grid item>
                        <IconButton size="small" onClick={() => handleRemoverDestino(item.id, destino.id)}>
                          <DeleteOutline />
                        </IconButton>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Custodiante"
                          value={destino.custodianteLabel || destino.custodianteCnpj}
                          onChange={(value) => handleCustodianteCnpjChange(item.id, destino.id, value)}
                          placeholder="Digite o CNPJ do custodiante"
                          error={!!destino.custodianteErro}
                          errorMessage={destino.custodianteErro}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Investidor"
                          value={destino.investidorLabel || destino.investidorCpfCnpj}
                          onChange={(value) => handleInvestidorCpfCnpjChange(item.id, destino.id, value)}
                          placeholder="Digite o CPF/CNPJ do investidor"
                          error={!!destino.investidorErro}
                          errorMessage={destino.investidorErro}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <BancoAutoComplete
                          bancos={bancos}
                          banco={destino.banco}
                          label="Banco"
                          onChange={(value) => handleUpdateDestino(item.id, destino.id, { banco: value })}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Dropdown
                          label="Modalidade de liquidação"
                          options={modalidadesLiquidacaoOptions}
                          value={destino.modalidadeLiquidacao}
                          onChange={(value) =>
                            handleUpdateDestino(item.id, destino.id, {
                              modalidadeLiquidacao: value as '' | 'BRUTA' | 'DIRETA',
                            })
                          }
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} alignItems="flex-end">
                      <Grid item xs={12} md={4}>
                        <NumberField
                          label="Quantidade"
                          value={destino.quantidade || undefined}
                          onChange={(value) => handleUpdateDestino(item.id, destino.id, { quantidade: value ?? 0 })}
                          numberType="integer"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <NumberField
                          label="PU (R$)"
                          value={destino.pu || undefined}
                          onChange={(value) => handleUpdateDestino(item.id, destino.id, { pu: value ?? 0 })}
                          numberType="eightDecimals"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            {'Total'}
                          </Typography>
                          <Typography variant="h6">{`R$ ${formatNumber(calcTotalDestino(destino))}`}</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box mt={2}>
                      <Typography variant="caption" color="textSecondary">
                        {'Comprovante (opcional)'}
                      </Typography>
                      <SmSpace />
                      <UploadDocs
                        onChange={(attachments: Attachment[]) => {
                          const novosArquivos = attachments.map((a) => a.file).filter((f): f is File => !!f);
                          if (novosArquivos.length > 0) {
                            handleUpdateDestino(item.id, destino.id, {
                              comprovantes: [...destino.comprovantes, ...novosArquivos],
                            });
                          }
                        }}
                        onDelete={() => {}}
                        multipleFiles={true}
                      />
                    </Box>
                  </Box>
                ))
              )}

              <Box textAlign="center" mt={2}>
                <Button variant="outlined" color="primary" onClick={() => handleAdicionarDestino(item.id)}>
                  {'Adicionar destino'}
                </Button>
              </Box>
            </Paper>
          ))
        )}
      </Section>
      <Section title="Resumo da operação" isFulfilled={() => true} defaultGrid={false}>
        {ifsCarrinho.length === 0 ? (
          <Box py={3} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              {'Nenhum IF adicionado ao carrinho.'}
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} style={{ marginBottom: '24px' }}>
              {[
                { label: 'IFs no carrinho', value: ifsCarrinho.length },
                { label: 'Total de destinos', value: ifsCarrinho.reduce((sum, item) => sum + item.destinos.length, 0) },
                {
                  label: 'Quantidade total',
                  value: formatIntNumber(ifsCarrinho.reduce((sum, item) => sum + calcAlocado(item.destinos), 0)),
                },
                {
                  label: 'Valor financeiro total',
                  value: `R$ ${formatNumber(ifsCarrinho.reduce((sum, item) => sum + calcTotalIF(item), 0))}`,
                },
              ].map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.label}>
                  <Paper elevation={2} style={{ padding: '16px', textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h5">
                      <strong>{card.value}</strong>
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>{'Código IF'}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>{'Destinos'}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{'Quantidade total'}</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{'Total (R$)'}</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ifsCarrinho.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.codigo}</TableCell>
                      <TableCell align="center">{item.destinos.length}</TableCell>
                      <TableCell align="right">{formatIntNumber(calcAlocado(item.destinos))}</TableCell>
                      <TableCell align="right">{`R$ ${formatNumber(calcTotalIF(item))}`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Section>

      <Box display="flex" justifyContent="flex-end" style={{ gap: '16px' }} mt={2} mb={4}>
        <Button
          variant="outlined"
          onClick={() => {
            clearCarrinho();
            history.goBack();
          }}
        >
          {'Cancelar'}
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!isFormValid() || submitting}>
          {submitting ? 'Enviando...' : 'Confirmar movimentação'}
        </Button>
      </Box>

      {feedbackData && (
        <VendaFeedbackModal
          open={feedbackOpen}
          onClose={() => {
            setFeedbackOpen(false);
            if (feedbackData.venda.status === 'SUCESSO') {
              history.push('/operacoes/gerenciar');
            }
          }}
          data={feedbackData}
        />
      )}
    </Box>
  );
};

export default CadastrarOperacaoVenda;
